import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createProduct, getProduct, updateProduct } from '../services/productsService';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import { useAuth } from '../context/AuthContext';

const EMPTY = {
  name: '',
  descripcion: '',
  id: '',
  impuesto: 0.18,
  price: '',
  costo: '',
  currency: 'PEN',
  unidad: 'NIU',
  image: '',
  activo: true,
  controlaStock: true,
  controlaLotes: false,
  stock: 0,
  stockMinimo: 0,
  stockMaximo: 0,
  codigoBarra: '',
  marca: '',
  modelo: '',
  numeroLote: '',
  fechaVencimiento: '',
};

export default function ProductFormPage() {
  const { uid } = useParams();
  const isEdit = Boolean(uid);
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      const p = await getProduct(uid);
      setForm({
        ...EMPTY,
        ...p,
        price: p.price ?? '',
        costo: p.costo ?? '',
        stock: p.stock ?? 0,
        stockMinimo: p.stockMinimo ?? 0,
        stockMaximo: p.stockMaximo ?? 0,
        numeroLote: '',
        fechaVencimiento: '',
      });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateProduct(uid, form);
        navigate(`/admin/productos/${uid}`);
      } else {
        const id = await createProduct({ ruc, data: form });
        navigate(`/admin/productos/${id}`);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Cargando producto…" />;

  return (
    <div className="page narrow">
      <header className="page-header">
        <div>
          <h1>{isEdit ? 'Editar producto' : 'Nuevo producto'}</h1>
          <p className="muted">Campos del modelo real · IGV 0.18 · price con IGV</p>
        </div>
      </header>

      <form className="form-card" onSubmit={onSubmit}>
        <div className="form-grid">
          <label>
            Nombre *
            <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </label>
          <label>
            Código interno
            <input value={form.id || ''} onChange={(e) => set('id', e.target.value)} />
          </label>
          <label className="span-2">
            Descripción
            <textarea value={form.descripcion || ''} onChange={(e) => set('descripcion', e.target.value)} rows={2} />
          </label>
          <label>
            Precio venta (con IGV) *
            <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => set('price', e.target.value)} required />
          </label>
          <label>
            Costo
            <input type="number" step="0.01" min="0" value={form.costo} onChange={(e) => set('costo', e.target.value)} />
          </label>
          <label>
            Impuesto
            <input type="number" step="0.01" min="0" max="1" value={form.impuesto} onChange={(e) => set('impuesto', e.target.value)} />
          </label>
          <label>
            Unidad
            <input value={form.unidad || 'NIU'} onChange={(e) => set('unidad', e.target.value)} />
          </label>
          <label>
            Marca
            <input value={form.marca || ''} onChange={(e) => set('marca', e.target.value)} />
          </label>
          <label>
            Modelo
            <input value={form.modelo || ''} onChange={(e) => set('modelo', e.target.value)} />
          </label>
          <label>
            Código de barra
            <input value={form.codigoBarra || ''} onChange={(e) => set('codigoBarra', e.target.value)} />
          </label>
          <label>
            Imagen URL
            <input value={form.image || ''} onChange={(e) => set('image', e.target.value)} />
          </label>
        </div>

        <div className="form-toggles">
          <label className="checkbox">
            <input type="checkbox" checked={!!form.controlaStock} onChange={(e) => set('controlaStock', e.target.checked)} />
            Controla stock
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={!!form.controlaLotes} onChange={(e) => set('controlaLotes', e.target.checked)} />
            Controla lotes
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={form.activo !== false} onChange={(e) => set('activo', e.target.checked)} />
            Activo
          </label>
        </div>

        {form.controlaStock ? (
          <div className="form-grid">
            <label>
              Stock
              <input type="number" min="0" value={form.stock} onChange={(e) => set('stock', e.target.value)} />
            </label>
            <label>
              Stock mínimo
              <input type="number" min="0" value={form.stockMinimo} onChange={(e) => set('stockMinimo', e.target.value)} />
            </label>
            <label>
              Stock máximo
              <input type="number" min="0" value={form.stockMaximo} onChange={(e) => set('stockMaximo', e.target.value)} />
            </label>
            {!isEdit && form.controlaLotes ? (
              <>
                <label>
                  Número de lote
                  <input value={form.numeroLote} onChange={(e) => set('numeroLote', e.target.value)} />
                </label>
                <label>
                  Fecha vencimiento
                  <input type="date" value={form.fechaVencimiento} onChange={(e) => set('fechaVencimiento', e.target.value)} />
                </label>
              </>
            ) : null}
          </div>
        ) : null}

        {error ? <div className="alert error">{error}</div> : null}

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar producto'}
          </button>
        </div>
      </form>
    </div>
  );
}
